import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { useSession } from '../../stores/session';
import type { Message } from '../messages/api';
import { MY_JAM_KEY, jams } from './api';
import { useMyJam } from './useJam';

/**
 * An invitation, in a message.
 *
 * A reference and not a link, so it can say what the jam is now: whose,
 * how many are in it, and whether it is still going. Join puts the reader
 * in and takes them to it; an ended jam says so and offers nothing.
 */
export function JamCard({
  jam,
  mine,
  compact = false,
}: {
  jam: NonNullable<Message['jam']>;
  mine: boolean;
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const me = useSession((state) => state.user);
  const { data } = useMyJam();
  const inIt = data?.jam?.id === jam.id;

  const join = useMutation({
    mutationFn: () => jams.join(jam.code),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: MY_JAM_KEY });
      navigate(`/jam/${jam.code}`);
    },
  });

  const ended = jam.status === 'ended';
  const hostIsMe = me?.id === jam.host.id;

  return (
    <div
      className={`mt-2 flex items-center gap-2.5 rounded-[var(--radius-control)] p-2 ${mine ? 'bg-black/20' : 'bg-app'}`}
      data-jam-card={jam.code}
      data-jam-card-status={jam.status}
    >
      <span
        aria-hidden
        className={`flex ${compact ? 'h-7 w-7' : 'h-9 w-9'} shrink-0 items-center justify-center rounded-[var(--radius-pill)] ${
          ended ? 'bg-line text-faint' : 'bg-fresh/15 text-fresh'
        }`}
      >
        <svg viewBox="0 0 16 16" className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M3 12V5.5l7-1.5v6.5M3 12a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0Zm7-1.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>

      <div className="min-w-0 flex-1 leading-tight">
        <span className={`block truncate font-medium ${compact ? 'text-[11px]' : 'text-xs'}`}>
          {ended ? 'Jam ended' : hostIsMe ? 'Your jam' : `${jam.host.display_name}'s jam`}
        </span>
        <span className={`block truncate opacity-70 ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
          {ended
            ? 'It is over.'
            : `${jam.member_count} ${jam.member_count === 1 ? 'person' : 'people'} in it`}
        </span>
      </div>

      {!ended && (inIt ? (
        <Link
          to={`/jam/${jam.code}`}
          className={`shrink-0 rounded-[var(--radius-pill)] border border-current px-2.5 py-1 font-mono ${compact ? 'text-[10px]' : 'text-[11px]'} opacity-80 hover:opacity-100`}
        >
          Open
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => join.mutate()}
          disabled={join.isPending}
          data-jam-card-join
          className={`shrink-0 rounded-[var(--radius-pill)] bg-accent px-2.5 py-1 font-mono ${compact ? 'text-[10px]' : 'text-[11px]'} text-accent-ink transition-colors hover:bg-accent-hover disabled:opacity-50`}
        >
          Join
        </button>
      ))}
    </div>
  );
}
